<script setup lang="ts">
interface ComponentProps {
  invoiceData: any;
}

const {invoiceData = {}} = defineProps<ComponentProps>();

const {t} = useI18n({
  useScope: 'global'
})

const isBatch = invoiceData.type === 'batch'

const columns = [
  {
    label: t('pdf.labels.saleDate'),
    value: 'saleDate',
    width: isBatch ? '9%' : '25%',
  },
  {
    label: t('pdf.labels.yearModel'),
    value: 'yearModel',
    width: isBatch ? '10%' : '25%',
  },
  {
    label: t('pdf.labels.vin'),
    value: 'vinCode',
    width: isBatch ? '17%' : '25%',
  },
  {
    label: t('pdf.labels.container'),
    value: 'containerNumber',
    width: isBatch ? '13%' : '13%',
  },
]

const financeColumns = []

if (!isBatch){
  financeColumns.push(
    {
      label: t('pdf.labels.description'),
      value: 'description',
      width: '80%',
    }
  )
  financeColumns.push(
    {
      label: t('pdf.labels.amount'),
      value: 'amount',
      width: '20%',
    }
  )
}

if (invoiceData.type !== 'single'){
  columns.push(
    {
      label: t('pdf.labels.carPrice'),
      value: 'auctionAmount',
      width: '10%',
    },
    {
      label: t('pdf.labels.domesticFee'),
      value: 'domesticFee',
      width: '10%',
    },
    {
      label: t('pdf.labels.cuttingFee'),
      value: 'cuttingFee',
      width: '10%',
    },
    {
      label: t('pdf.labels.transportationFee'),
      value: 'transportationFee',
      width: '10%',
    },
    {
      label: t('pdf.labels.total'),
      value: 'total',
      width: '10%',
    },
  )
}

const formatAmount = (amount: number) => {
  if (!amount && amount !== 0) return ''
  return `${amount.toFixed(2)} USD`
}

const formatAmountWithCommand = (amount: number, command: string) => {
  const str = command === '+' ? '' : '- '
  if (!amount && amount !== 0) return ''
  return `${str}${amount.toFixed(2)} USD`
}

const financeData = computed(() => {
  const invoice = unref(invoiceData)
  if (invoice.type === 'single'){
    const car = invoice.list[0]
    const financialInfo = car.financialInfo

    const formattedData = Object.entries(financialInfo).filter(([key, {value}]) => {
      if (['balance', 'amountBalance', 'amountPaid', 'paidDealerComission', 'feeOnCar', 'feeOnTransportation'].includes(key)){
        return false
      }

      return !!value;

    }).map(([key, {value,command}]) => ({
      description: t(`car.financialInfo.${key}`),
      amount: formatAmountWithCommand(value, command),
      // classes: command === '+'? 'positive' : 'negative'
    }))

    formattedData.push({})

    return formattedData
  }
  return []
})

const getRowClass = (row: any) => {
  if (row.subtotal) {
    return 'subtotal'
  }
  return ''
}

</script>

<template>
  <div>
    <PdfPartsTable
      class="main-table"
      :class="{ 'single': invoiceData.type === 'single', batch: invoiceData.type === 'batch', subtotal: invoiceData.enableSubtotal }"
      :columns="columns"
      :data="invoiceData.list"
      :row-class="getRowClass"
    >
      <template #cell:saleDate="{row}">
        <FormatDate
          v-if="row.saleDate"
          format="LLLL dd, yyyy"
          :date="row.saleDate"
        />
        <template
          v-if="row.subtotal"
        >
          {{ t('pdf.labels.table.subtotal') }}
        </template>
      </template>
      <template #cell:yearModel="{row}">
        {{ row.carDescription }} {{ row.car?.year }}
      </template>

      <template #cell:auctionAmount="{row}">
        {{ invoiceData.showingParts.auctionPrice ? formatAmount(row.auctionAmount) : '' }}
      </template>
      <template #cell:domesticFee="{row}">
        {{ invoiceData.showingParts.transportationPrice ? formatAmount(row.domesticFee) : '' }}
      </template>
      <template #cell:cuttingFee="{row}">
        {{ invoiceData.showingParts.cuttingPrice ? formatAmount(row.cuttingAmount) : '' }}
      </template>
      <template #cell:transportationFee="{row}">
        {{ invoiceData.showingParts.transportationPrice ? formatAmount(row.transportationAmount) : '' }}
      </template>
      <template #cell:total="{row}">
        {{ formatAmount(row.total) }}
      </template>
      <template #footer>
        <div v-if="isBatch">
          <div
            v-if="invoiceData.insuranceContainerFee"
            class="table__footer"
          >
            <span class="total">{{ t('pdf.labels.table.insuranceContainerFee') }}</span>
            <span class="sum">{{ formatAmount(invoiceData.insuranceContainerFee) }}</span>
          </div>
          <div class="table__footer">
            <span class="total">{{ t('pdf.labels.table.total') }}</span>
            <span class="sum">{{ formatAmount(invoiceData.total) }}</span>
          </div>
          <div
            v-if="invoiceData.amountSale"
            class="table__footer"
          >
            <span class="total">{{ t('pdf.labels.table.amountSale') }}</span>
            <span class="sum">{{ formatAmount(-invoiceData.amountSale) }}</span>
          </div>
          <div class="table__footer">
            <span class="total">{{ t('pdf.labels.table.totalPaid') }}</span>
            <span class="sum">{{ formatAmount(-invoiceData.totalPaid) }}</span>
          </div>
          <div class="table__footer">
            <span class="total">{{ t('pdf.labels.table.totalToPay') }}</span>
            <span class="sum">{{ formatAmount(invoiceData.totalToPay) }}</span>
          </div>
        </div>
      </template>
    </PdfPartsTable>
    <PdfPartsTable
      v-if="invoiceData.type === 'single'"
      class="finance-info-table"
      :class="{ 'single': invoiceData.type === 'single', batch: invoiceData.type === 'batch' }"
      :columns="financeColumns"
      :data="financeData"
    >
      <template #cell:amount="{row}">
        <span>{{ row.amount }}</span>
      </template>
      <template #footer>
        <div>
          <div class="table__footer">
            <span class="total">{{ t('pdf.labels.table.total') }}</span>
            <span class="sum">{{ formatAmount(invoiceData.total) }}</span>
          </div>
          <div
            v-if="invoiceData.amountSale"
            class="table__footer"
          >
            <span class="total">{{ t('pdf.labels.table.amountSale') }}</span>
            <span class="sum">{{ formatAmount(-invoiceData.amountSale) }}</span>
          </div>
          <div class="table__footer">
            <span class="total">{{ t('pdf.labels.table.totalPaid') }}</span>
            <span class="sum">{{ formatAmount(-invoiceData.totalPaid) }}</span>
          </div>
          <div class="table__footer">
            <span class="total">{{ t('pdf.labels.table.totalToPay') }}</span>
            <span class="sum">{{ formatAmount(invoiceData.totalToPay) }}</span>
          </div>
        </div>
      </template>
    </PdfPartsTable>
    <ConsoleLog>
      {{ invoiceData }}
    </ConsoleLog>
  </div>
</template>

<style lang="scss" scoped>
.finance-info-table{
  margin-bottom: 30px;
  .table__footer{
    justify-content: end;
    gap: 10%;
    :deep(.sum){
      min-width: 120px;
      text-align: right;
    }
    :deep(.total) {
      width: 15.6%;
    }
  }
}

.main-table {
  margin-bottom: 30px;
  &.subtotal {
    :deep(.table__body){
      padding-bottom: 0;
    }
    :deep(.subtotal) {
      padding: 7px 10px;
      padding-top: 15px;
      font-weight: 700;
      border: 1px solid rgba(45, 45, 134, 0.2);
      border-left: none;
      border-right: none;
      border-bottom: none;
      .table__row {
        margin: 0;
        padding: 0;
      }
      .saleDate{
        margin: 0;
        padding: 0;
      }
    }
  }
  :deep(.table__body){
    &::after {
      display: none;
    }
  }
}

.finance-info{
  :deep(.item){
    justify-content: space-between;
    font-size: 10px;
    margin-bottom: 2px;
  }

  :deep(.sum .item-title){
    font-size: 8px;
  }
}

:deep(.table__column) {
  &.containerNumber {
    flex: 1;
  }

  &.description {
    font-size: 10px;
  }

  &.amount {
    width: 10%;
    text-align: right;
    font-size: 10px;
  }

  &.total {
    width: 10%;
    text-align: right;
    padding-right: 0;
  }
}


.table {
  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 10px;
    font-weight: 700;
    border: 1px solid rgba(45, 45, 134, 0.2);
    border-left: none;
    border-right: none;

    .total {
      width: 19.6%;
    }
  }
}
.single {
  :deep(.table__body) {
    &::after {
      width: 20%;
    }
  }
}

.batch {
  :deep(.table__body) {
    &::after {
      width: 12%;
    }
  }
}
</style>
